import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "@headlessui/react";
import { Channel } from "../../../../../lib/api/channels";
import { BoardJournal } from "../../../../../stores/boardStore";
import { SectionLoader, ButtonSpinner, Spinner } from "../../../../../components/LogoLoader";

interface ChannelSelectionViewProps {
    channels: Channel[];
    journals?: BoardJournal[];

    isLoading: boolean;
    isAnimating: boolean;
    onChannelClick: (channel: Channel) => void;
    onJournalClick: (journal: BoardJournal) => void;
    onAddNewChannel: () => void;
    onAddJournal: () => void;
    onValidate: () => void;
    isValidating: boolean;
    selectedCount: number;
    onClose: () => void;
    addedChannelIds?: string[];
    totalValue?: number;
    boardCurrency?: string;
}

export const ChannelSelectionView: React.FC<ChannelSelectionViewProps> = ({
    channels,
    journals = [],
    isLoading,
    isAnimating,
    onChannelClick,
    onJournalClick,
    onAddNewChannel,
    onAddJournal,
    onValidate,
    isValidating,
    selectedCount,
    onClose,
    addedChannelIds = [],
    totalValue = 0,
    boardCurrency,
}) => {
    const { t, i18n } = useTranslation("common");

    // Status text helper
    const getStatusText = (status: string, isAdded: boolean = false): string => {
        if (isAdded) return t("common.added", "Added");
        const statusMap: Record<string, string> = {
            active: t("channels.active"),
            syncing: t("channels.syncing"),
            error: t("channels.error"),
            inactive: t("channels.inactive"),
        };
        return statusMap[status] || status;
    };

    // Render channel icon
    const renderChannelIcon = (channel: Channel) => {
        if (channel.icon && (channel.icon.startsWith("http") || channel.icon.startsWith("/"))) {
            return <img src={channel.icon} alt={channel.name} className="w-7 h-7 object-contain" />;
        }
        if (channel.channelTypeConfig?.icon && (channel.channelTypeConfig.icon.startsWith("/") || channel.channelTypeConfig.icon.startsWith("http"))) {
            return <img src={channel.channelTypeConfig.icon} alt={channel.name} className="w-7 h-7 object-contain" />;
        }
        if (channel.icon) {
            return <span className="text-xl leading-none">{channel.icon}</span>;
        }
        return (
            <span className="w-6 h-6 text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_4e2f2f0b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4e2f2f0b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
        );
    };

    // Render status badge
    const renderStatusBadge = (status: string, isAdded: boolean = false) => {
        if (isAdded) {
            return (
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 border border-neutral-200 dark:border-neutral-700">
                    <span className="w-3 h-3 text-neutral-500 dark:text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_c4211b66.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_c4211b66.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    {getStatusText(status, true)}
                </div>
            );
        }

        const statusLower = status.toLowerCase();
        const baseClasses = "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium";
        const statusClasses = statusLower === "active"
            ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-800/50"
            : statusLower === "error"
                ? "bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 border border-rose-100 dark:border-rose-800/50"
                : "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-100 dark:border-amber-800/50";

        return (
            <div className={`${baseClasses} ${statusClasses}`}>
                {statusLower === "syncing" ? (
                    <Spinner size="xs" className="text-current" />
                ) : (
                    <div className={`w-1.5 h-1.5 rounded-full ${statusLower === "active" ? "bg-emerald-500 dark:bg-emerald-400" :
                        statusLower === "error" ? "bg-rose-500 dark:bg-rose-400" : "bg-amber-500 dark:bg-amber-400"
                        }`} />
                )}
                {getStatusText(statusLower)}
            </div>
        );
    };

    return (
        <>
            {/* Header */}
            <div className="px-6 py-5 border-b border-neutral-200 dark:border-neutral-800">
                <div className="flex items-center justify-between">
                    <div className="min-w-0">
                        <Dialog.Title as="h2" className="text-xl font-bold text-neutral-900 dark:text-neutral-100 truncate">
                            {t("connections.title")}
                        </Dialog.Title>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 truncate">
                            {t("itemSelection.selectEntitiesDescription")}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="flex-shrink-0 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg focus:outline-none"
                    >
                        <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_4a9e8065.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4a9e8065.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(85vh - 140px)' }}>
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <SectionLoader size="sm" message={String(t("channels.loadingChannels"))} />
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Channels Section */}
                        <div className="space-y-3">
                            <h3 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider px-1">{t("channels.myChannels")}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {channels.map((channel) => {
                                    const isAdded = addedChannelIds.includes(channel.id);
                                    const isPseudoChannelEnabled = channel.channelTypeConfig?.pseudoChannelEnabled === true;
                                    const isDisabled = isAnimating || channel.status === 'syncing' || (isAdded && !isPseudoChannelEnabled);

                                    return (
                                        <button
                                            key={`channel-${channel.id}`}
                                            onClick={() => onChannelClick(channel)}
                                            disabled={isDisabled}
                                            className={`group p-4 border border-neutral-200 dark:border-neutral-800 rounded-2xl transition-all text-left bg-white dark:bg-neutral-900 hover:border-accent-primary/30 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-4 ${(channel as any).isEnabled === false ? 'opacity-60' : ''}`}
                                        >
                                            <div className="w-12 h-12 rounded-full bg-white dark:bg-neutral-800 border border-neutral-100 dark:border-neutral-700 flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105 shadow-sm overflow-hidden">
                                                {renderChannelIcon(channel)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-neutral-600 dark:text-neutral-300 mb-0.5 group-hover:text-accent-primary transition-colors truncate">
                                                    {channel.name}
                                                </div>
                                                <div className="text-xs text-neutral-400 dark:text-neutral-500 font-medium truncate">{channel.channelType}</div>
                                                <div className="flex items-center gap-2 mt-1.5">
                                                    {renderStatusBadge(channel.status, isAdded && !isPseudoChannelEnabled)}
                                                </div>
                                            </div>
                                            {(isAdded && !isPseudoChannelEnabled) ? (
                                                <div className="w-5 h-5 bg-neutral-100 dark:bg-neutral-800 rounded-full flex items-center justify-center flex-shrink-0">
                                                    <span className="w-3 h-3 text-neutral-500 dark:text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_02c569e3.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_02c569e3.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                </div>
                                            ) : (
                                                <span className="w-5 h-5 text-neutral-300 group-hover:text-accent-primary group-hover:translate-x-0.5 transition-all flex-shrink-0" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_c82c8f45.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_c82c8f45.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            )}
                                        </button>
                                    );
                                })}

                                {/* Add Channel Card */}
                                <button
                                    id="onboarding-add-channel-modal"
                                    onClick={onAddNewChannel}
                                    className="group p-4 border border-dashed border-neutral-300 dark:border-neutral-700 rounded-2xl hover:border-accent-primary hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all flex flex-col items-center justify-center text-center gap-3 h-full min-h-[100px]"
                                >
                                    <div className="w-12 h-12 rounded-full bg-neutral-100 dark:bg-neutral-800 group-hover:bg-white dark:group-hover:bg-neutral-700 group-hover:shadow-sm border border-transparent group-hover:border-neutral-100 dark:group-hover:border-neutral-600 flex items-center justify-center transition-all">
                                        <span className="w-6 h-6 text-neutral-400 dark:text-neutral-500 group-hover:text-accent-primary transition-colors" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_477c11a4.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_477c11a4.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    </div>
                                    <div>
                                        <div className="font-bold text-neutral-600 dark:text-neutral-300 group-hover:text-accent-primary transition-colors">
                                            {t("channels.addChannel")}
                                        </div>
                                        <div className="text-xs text-neutral-400 dark:text-neutral-500 font-medium mt-0.5">{t("channels.connectChannel")}</div>
                                    </div>
                                </button>
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="relative py-2">
                            <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                <div className="w-full border-t border-neutral-200 dark:border-neutral-800" />
                            </div>
                        </div>

                        {/* Journals Section */}
                        <div className="space-y-3">
                            <h3 className="text-xs font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider px-1">{t("navigation.moneyJournal")}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {journals.map((journal) => {
                                    return (
                                        <button
                                            key={`journal-${journal.id}`}
                                            onClick={() => onJournalClick(journal)}
                                            disabled={isAnimating}
                                            className={`group p-4 border border-neutral-200 dark:border-neutral-800 rounded-2xl transition-all text-left bg-white dark:bg-neutral-900 hover:border-accent-primary/30 hover:shadow-md disabled:opacity-50 flex items-center gap-4 ${journal.isEnabled === false ? 'opacity-60' : ''}`}
                                        >
                                            <div className="w-12 h-12 rounded-full bg-white dark:bg-neutral-800 border border-neutral-100 dark:border-neutral-700 flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105 shadow-sm overflow-hidden text-neutral-400">
                                                <span className="w-6 h-6 group-hover:text-accent-primary transition-colors" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_5cdba0c2.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_5cdba0c2.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-neutral-600 dark:text-neutral-300 mb-0.5 group-hover:text-accent-primary transition-colors truncate">
                                                    {journal.name}
                                                </div>
                                                <div className="text-xs text-neutral-400 dark:text-neutral-500 font-medium truncate">{t("navigation.moneyJournal")}</div>
                                            </div>
                                            <span className="w-5 h-5 text-neutral-300 group-hover:text-accent-primary group-hover:translate-x-0.5 transition-all flex-shrink-0" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_5b1d2ef3.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_5b1d2ef3.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                        </button>
                                    );
                                })}

                                {/* Add Journal Card - Changed to match Add Channel button style */}
                                <button
                                    id="onboarding-add-journal-modal-list"
                                    onClick={onAddJournal}
                                    className="group p-4 border border-dashed border-neutral-300 dark:border-neutral-700 rounded-2xl hover:border-accent-primary hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all flex flex-col items-center justify-center text-center gap-3 h-full min-h-[100px]"
                                >
                                    <div className="w-12 h-12 rounded-full bg-neutral-100 dark:bg-neutral-800 group-hover:bg-white dark:group-hover:bg-neutral-700 group-hover:shadow-sm border border-transparent group-hover:border-neutral-100 dark:group-hover:border-neutral-600 flex items-center justify-center transition-all">
                                        <span className="w-6 h-6 text-neutral-400 dark:text-neutral-500 group-hover:text-accent-primary transition-colors" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_477c11a4.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_477c11a4.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    </div>
                                    <div>
                                        <div className="font-bold text-neutral-600 dark:text-neutral-300 group-hover:text-accent-primary transition-colors">
                                            {t("boardJournals.addJournal")}
                                        </div>
                                        <div className="text-xs text-neutral-400 dark:text-neutral-500 font-medium mt-0.5">{t("boardJournals.createJournal")}</div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50 rounded-b-2xl">
                <div className="flex justify-between items-center">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-0 sm:gap-1.5 whitespace-nowrap justify-center min-w-0">
                        <span className="text-xs text-neutral-500 dark:text-neutral-400 leading-tight">
                            {selectedCount} {t("common.selected")}
                        </span>
                        <span className={`text-xs font-bold leading-tight ${totalValue > 0 ? 'text-emerald-600 dark:text-emerald-400' : totalValue < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-neutral-500 dark:text-neutral-400'}`}>
                            {Math.abs(totalValue).toLocaleString(i18n.language, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} {
                                boardCurrency ? (0).toLocaleString(i18n.language, { style: 'currency', currency: boardCurrency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/[\d\s.,]/g, '') : '$'
                            }
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                        >
                            {t("actions.cancel")}
                        </button>
                        <button
                            onClick={onValidate}
                            disabled={isValidating}
                            className="px-4 py-2 text-sm font-medium text-white bg-accent-primary hover:bg-accent-primary-hover rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                            {isValidating ? (
                                <>
                                    <ButtonSpinner />
                                    {t("actions.loading")}
                                </>
                            ) : (
                                t("itemSelection.validate")
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};
